import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { migrationApi, type MigrationCaseEventDto } from '@/lib/api';
import {
  formatMigrationEventLabel,
  formatMigrationEventTimestamp,
  isDisplayableMigrationEvent,
} from '@/lib/migrationEventDisplay';

type Props = {
  open: boolean;
  caseId: number | null;
  onOpenChange: (open: boolean) => void;
};

export default function MigrationCaseHistoryModal({ open, caseId, onOpenChange }: Readonly<Props>) {
  const [events, setEvents] = useState<MigrationCaseEventDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !caseId) return;
    let active = true;
    setIsLoading(true);
    setError(null);
    migrationApi
      .getCaseEvents(caseId)
      .then((res) => {
        if (active) setEvents(res);
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'Historie konnte nicht geladen werden');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, caseId]);

  const displayedEvents = useMemo(
    () => events.filter((ev) => isDisplayableMigrationEvent(ev.event_type)),
    [events]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>Ereignis-Historie</DialogTitle>
          <DialogDescription>Alle relevanten Ereignisse zu diesem Migrations-Case.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Lade…</p>
          ) : error ? (
            <p className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{error}</p>
          ) : displayedEvents.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Noch keine Ereignisse.</p>
          ) : (
            <ul className="space-y-2">
              {displayedEvents.map((ev) => {
                const label = formatMigrationEventLabel(ev);
                return (
                  <li key={ev.id} className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{label}</span>
                      <span className="text-xs text-muted-foreground">{formatMigrationEventTimestamp(ev.created_at)}</span>
                    </div>
                    {ev.actor_name ? (
                      <p className="mt-1 text-xs text-muted-foreground">von {ev.actor_name}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex justify-end pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
